import React from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";

/** One step of the trail. The page you are on carries no link. */
export interface Crumb {
  label: string;
  /** Where the step goes. Left off for the current page. */
  to?: string;
}

interface PageHeaderProps {
  /**
   * What the page is called. A node rather than a string only because one
   * screen makes its title editable in place — see `PlanNameField`; every
   * other one passes the text.
   */
  title: React.ReactNode;
  /**
   * Set beside the title, inside the same heading — the plan number after a
   * plan's name. Part of the heading rather than a line under it, because it
   * is part of what the page is called.
   */
  titleSuffix?: React.ReactNode;
  /**
   * The trail back to where this screen was opened from, shown under the
   * title. Only screens that sit under another one pass it — on a section's
   * own page the trail would say no more than the title already does.
   */
  breadcrumbs?: Crumb[];
}

/** Title row shared by every signed-in screen: page name left, bell right. */
const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleSuffix,
  breadcrumbs,
}) => (
  <header className="flex-shrink-0 flex items-center justify-between gap-4">
    <div className="min-w-0">
      <h1 className="flex min-w-0 items-baseline gap-2 text-[18px] sm:text-[20px] 3xl:text-[24px] font-medium text-ink">
        <span className="truncate">{title}</span>
        {titleSuffix}
      </h1>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mt-1">
          <ol className="flex flex-wrap items-center gap-1 text-[12px] 3xl:text-[13px] text-[#80828D]">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.label} className="flex items-center gap-1">
                {crumb.to ? (
                  <Link
                    to={crumb.to}
                    className="hover:text-brand transition-colors"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  // The last step names the page you are already on, so it is
                  // read out as the current one rather than offered as a way
                  // to somewhere else.
                  <span aria-current="page">{crumb.label}</span>
                )}
                {index < breadcrumbs.length - 1 && (
                  <span aria-hidden="true">&gt;</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
    </div>
    <button
      type="button"
      aria-label="Notifications"
      className="flex-shrink-0 p-2 rounded-lg text-ink-muted hover:bg-[#F4F7F8] hover:text-ink transition-colors"
    >
      <Bell className="w-5 h-5 3xl:w-6 3xl:h-6" />
    </button>
  </header>
);

export default PageHeader;
